// ============================================================================
// LIT-CASE — Expenses section
// ============================================================================
// Per-case slice of expense allocations. Lists every allocated share against
// this case, with rollups (total / reimbursable / non-reimbursable / by head).
// "Add expense" opens the voucher form modal pre-filled with this case as a
// DIRECT allocation. Source: GET /Expense/by-case/{caseId}.

import React, { useCallback, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useCaseExpenses } from 'hooks/api/useExpenses';
import type { LitigationCase } from 'api/types/litigationCase.types';
import type { ExpenseAllocationView } from 'api/types/expense.types';
import ExpenseFormModal from 'components/modals/modules/expense-module/ExpenseFormModal';

interface Props {
  kase: LitigationCase;
  onMutated: () => void;
}

const fmtDate = (s?: string | null) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('en-IN'); } catch { return s; } };
const fmtAmount = (n?: number | null) =>
  n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CaseExpensesSection: React.FC<Props> = ({ kase, onMutated }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: rows, loading, error } = useCaseExpenses(kase.id, refreshKey);
  const [formOpen, setFormOpen] = useState(false);

  const refresh = useCallback(() => { setRefreshKey(k => k + 1); onMutated(); }, [onMutated]);

  const totals = useMemo(() => {
    const total = rows.reduce((s, r) => s + (r.allocatedAmount || 0), 0);
    const reimb = rows.filter(r => r.isReimbursable).reduce((s, r) => s + (r.allocatedAmount || 0), 0);
    const nonReimb = total - reimb;
    const byHead = new Map<string, number>();
    for (const r of rows) {
      const key = r.expenseName || r.expenseCode || 'Other';
      byHead.set(key, (byHead.get(key) ?? 0) + (r.allocatedAmount || 0));
    }
    return { total, reimb, nonReimb, byHead };
  }, [rows]);

  if (loading && rows.length === 0) {
    return <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>Loading expenses…</div>;
  }
  if (error) {
    return <div style={{ padding: '1.5rem', color: '#dc2626' }}>Failed to load: {error.message}</div>;
  }

  return (
    <div style={{ padding: '1rem 1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.85rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>
          Expenses ({rows.length})
        </h3>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => setFormOpen(true)}
          style={{ fontSize: '0.8rem', padding: '0.4rem 0.85rem' }}
        >
          <IconSystem.add size={14} /> Add Expense
        </button>
      </div>

      {/* Rollup cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
        <StatCard label="Total allocated"   value={fmtAmount(totals.total)} />
        <StatCard label="Reimbursable"      value={fmtAmount(totals.reimb)} accent="#16a34a" />
        <StatCard label="Non-reimbursable"  value={fmtAmount(totals.nonReimb)} accent="#6b7280" />
      </div>

      {/* By-head breakdown */}
      {totals.byHead.size > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '0.4rem',
          marginBottom: '0.85rem', fontSize: '0.78rem',
        }}>
          {Array.from(totals.byHead.entries()).map(([head, amt]) => (
            <span key={head} style={{
              padding: '0.25rem 0.55rem', borderRadius: 4,
              background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
            }}>
              {head}: <strong style={{ color: 'var(--text-primary)' }}>{fmtAmount(amt)}</strong>
            </span>
          ))}
        </div>
      )}

      {/* Allocations table */}
      {rows.length === 0 ? (
        <div style={{
          padding: '2rem', textAlign: 'center', color: 'var(--text-muted)',
          border: '1px dashed var(--border-color)', borderRadius: 6,
        }}>
          <IconSystem.dollarSign size={28} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '0.9rem' }}>No expenses allocated to this case yet.</div>
          <div style={{ fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Click <strong>Add Expense</strong> above to record one.
          </div>
        </div>
      ) : (
        <div style={{
          border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
              <tr>
                <th style={th('140px')}>Txn No</th>
                <th style={th('100px')}>Date</th>
                <th style={th('180px')}>Head</th>
                <th style={th()}>Paid To</th>
                <th style={th('100px')}>Mode</th>
                <th style={th('80px')}>Reimb?</th>
                <th style={{ ...th('120px'), textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => <AllocationRow key={r.allocationId} r={r} />)}
              <tr style={{ background: 'var(--bg-secondary)', borderTop: '2px solid var(--border-color)' }}>
                <td colSpan={6} style={{ ...td(), fontWeight: 600 }}>Total</td>
                <td style={{
                  ...td(), textAlign: 'right', fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {fmtAmount(totals.total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Add expense modal — pre-filled with this case as the only DIRECT allocation */}
      <ExpenseFormModal
        isOpen={formOpen}
        mode="create"
        prefill={{
          caseId: kase.id,
          caseCode: kase.caseCode,
          caseTypeCode: kase.caseTypeCode,
          customerName: kase.customerName,
          accountNumber: kase.accountNumber,
        }}
        onSaved={refresh}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
};

const AllocationRow: React.FC<{ r: ExpenseAllocationView }> = ({ r }) => (
  <tr style={{ borderTop: '1px solid var(--border-color)' }}>
    <td style={td()}><strong>{r.transactionNo}</strong></td>
    <td style={td()}>{fmtDate(r.transactionDate)}</td>
    <td style={td()}>{r.expenseName || r.expenseCode}</td>
    <td style={td()}>{r.paidTo || '—'}</td>
    <td style={td()}>
      <span style={{
        fontSize: '0.72rem',
        padding: '1px 6px',
        borderRadius: 4,
        background: r.allocationMode === 'DIRECT' ? '#dbeafe' : '#fef3c7',
        color: r.allocationMode === 'DIRECT' ? '#1e40af' : '#92400e',
      }}>
        {r.allocationMode}
      </span>
    </td>
    <td style={td()}>
      {r.isReimbursable
        ? <span style={{ color: '#16a34a', fontSize: '0.78rem' }}>Yes</span>
        : <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>No</span>}
    </td>
    <td style={{ ...td(), textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
      {fmtAmount(r.allocatedAmount)}
    </td>
  </tr>
);

const StatCard: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <div style={{
    padding: '0.6rem 0.75rem',
    border: '1px solid var(--border-color)', borderRadius: 6,
    background: 'var(--bg-primary)',
  }}>
    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </div>
    <div style={{
      fontSize: '1.05rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
      color: accent || 'var(--text-primary)', marginTop: '0.2rem',
    }}>
      {value}
    </div>
  </div>
);

const th = (width?: string): React.CSSProperties => ({
  padding: '0.5rem 0.625rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.78rem',
  borderBottom: '2px solid var(--border-color)',
  whiteSpace: 'nowrap',
  width,
});

const td = (): React.CSSProperties => ({
  padding: '0.45rem 0.625rem',
  verticalAlign: 'middle',
});

export default CaseExpensesSection;
